import {
    MDBBtn,
    MDBCol,
    MDBCollapse,
    MDBContainer,
    MDBIcon,
    MDBNavbar,
    MDBNavbarBrand,
    MDBNavbarItem,
    MDBNavbarLink,
    MDBNavbarNav,
    MDBNavbarToggler,
} from "mdb-react-ui-kit";
import React, {
    Dispatch,
    forwardRef,
    ReactElement,
    SetStateAction,
    useState,
} from "react";
import { useRouter } from "next/router";
import Image from "next/image";
import styles from "styles/navbar/NavBar.module.scss";
import { useStore } from "@/store/index";
import useWishlistData from "@/utils/queries/hooks/useWishlistData";
import { randomUid } from "@/utils/randomIds";
import { DropDownItems } from "@/models/common/enums";
import userInterfaceSvg from "@/utils/svg-icon/userInterface";
import contactsSvg from "@/utils/svg-icon/contacts";
import generalSvg from "@/utils/svg-icon/general";
import CurrencySelector from "@/components/common/CurrencySelector";
import NavSubMenu from "./NavSubMenu";
// import DropdownMiniMenu from "./DropdownMiniMenu";

const NavBar = forwardRef<
    HTMLDivElement,
    {
        dropdownHandler: (arg: DropDownItems) => void;
        isSmallDropdownOpen: boolean;
        isBigDropdownOpen: boolean;
        closeBigDropdown: () => void;
        closeSmallDropDown: () => void;
        setShowSearchModal: Dispatch<SetStateAction<boolean>>;
        ctaButtonText?: string;
        ctaButtonUrl?: string;
    }
>(
    (
        {
            dropdownHandler,
            isSmallDropdownOpen,
            isBigDropdownOpen,
            closeBigDropdown,
            closeSmallDropDown,
            setShowSearchModal,
            ctaButtonText,
            ctaButtonUrl,
        },
        ref
        // eslint-disable-next-line sonarjs/cognitive-complexity
    ): ReactElement => {
        const router = useRouter();
        const openModalAuth = useStore(state => state.openModalAuth);
        const openModalFavourite = useStore(state => state.openModalFavourite);
        // eslint-disable-next-line no-underscore-dangle
        const userId = useStore(state => state.userState.userDetail._id);
        const { refetch } = useWishlistData();
        const { SearchLine } = generalSvg;

        const handleSearchClick = (e?: React.MouseEvent) => {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            // Apri sempre il modal di ricerca, indipendentemente dalla pagina
            setShowSearchModal(true);
        };

        const handleWishlist = () => {
            if (userId) {
                refetch();
            }
            openModalFavourite();
        };

        const handleCtaButtonClick = () => {
            if (ctaButtonUrl) {
                window.open(ctaButtonUrl, "_blank");
            }
        };

        // Mostra il bottone solo se entrambi i valori CMS sono presenti
        const showCtaButton = !!ctaButtonText && !!ctaButtonUrl;

        const buttonIcons = [
            {
                icon: userInterfaceSvg.HeartLine,
                id: randomUid(),
                onClick: handleWishlist,
            },
            {
                icon: contactsSvg.User,
                id: randomUid(),
                onClick: openModalAuth,
            },
        ];

        const standardLink = [
            {
                title: "Italian stories",
                id: randomUid(),
                path: "/italian-stories",
            },
            {
                title: "Contacts",
                id: randomUid(),
                path: "/contacts",
            },
        ];

        const dropdownItems = ["About us", "How it works"];
        const [showNavExternal, setShowNavExternal] = useState(false);
        const [showLateralMenu, setShowLateralMenu] = useState(false);

        return (
            <MDBNavbar
                className="d-flex justify-content-center py-3 shadow-0 displayDesktop"
                expand="lg"
                dark
                bgColor="light"
            >
                <MDBContainer className="mx-lg-0 mx-xl-1 mx-xxl-4 px-0 mx-0 mw-100 pe-4">
                    <MDBCol
                        sm={3}
                        className={`${styles.mobile} justify-content-center px-4`}
                    >
                        <MDBNavbarToggler
                            className="p-0"
                            type="button"
                            data-target="#navbarColor02"
                            aria-controls="navbarColor02"
                            aria-expanded="false"
                            aria-label="Toggle navigation"
                            onClick={() => setShowNavExternal(!showNavExternal)}
                        >
                            <MDBIcon
                                className={`${styles.overlay} square rounded-7 border border-2 p-3 shadow-0 border-primary text-primary`}
                                icon="bars"
                                fas
                            />
                        </MDBNavbarToggler>
                    </MDBCol>
                    <MDBCol
                        sm={3}
                        lg={2}
                        className="d-inline-flex d-xl-flex justify-content-center ps-xl-2"
                    >
                        <MDBNavbarBrand
                            style={{ cursor: "pointer" }}
                            id="play-italy-logo"
                            className={`${styles.vertical_divider_right} align-self-center mx-0 text-primary fw-bold`}
                            onClick={() => router.push(`/`)}
                        >
                            <Image
                                className=" mx-4"
                                src="/logo.svg"
                                alt="Logo"
                                width={120}
                                height={40}
                            />
                        </MDBNavbarBrand>
                    </MDBCol>
                    <MDBCol
                        sm={3}
                        className={`${styles.mobile} justify-content-center px-4`}
                    >
                        <MDBIcon
                            className={`${styles.overlay} top_icons square rounded-7 border border-2 p-3 shadow-0`}
                            fas
                            icon="search"
                        />
                    </MDBCol>
                    <MDBCollapse
                        className={`${styles.overlay} mt-lg-0 mt-3`}
                        show={showNavExternal}
                        navbar
                    >
                        <MDBNavbarNav className="d-inline-flex me-auto mb-2 mb-lg-0 px-0">
                            <MDBCol
                                xl={7}
                                className={`${
                                    !showLateralMenu ? styles.lateralMenu : ""
                                }  d-flex flex-column flex-lg-row py-3 py-lg-0 py-xl-2`}
                            >
                                <div
                                    ref={ref}
                                    className="d-flex align-items-center"
                                >
                                    <div
                                        className={`${styles.linkText} nav-link`}
                                    >
                                        <p
                                            onMouseEnter={() =>
                                                dropdownHandler(
                                                    DropDownItems.TOURS
                                                )
                                            }
                                            className={`${styles.paragraphStyle} my-0 dropdown-toggle `}
                                        >
                                            tours
                                        </p>
                                    </div>
                                    <div
                                        className={`${styles.linkText} nav-link`}
                                    >
                                        <p
                                            onMouseEnter={() =>
                                                dropdownHandler(
                                                    DropDownItems.DESTINATIONS
                                                )
                                            }
                                            className={`${styles.paragraphStyle} my-0 dropdown-toggle`}
                                        >
                                            destinations
                                        </p>
                                    </div>
                                    <div
                                        className={`${styles.linkText} nav-link`}
                                        onClick={() =>
                                            router.push("/how-it-works")
                                        }
                                    >
                                        <p
                                            className={`${styles.paragraphStyle} my-0`}
                                            onMouseEnter={() =>
                                                isSmallDropdownOpen
                                                    ? closeSmallDropDown()
                                                    : isBigDropdownOpen
                                                    ? closeBigDropdown()
                                                    : null
                                            }
                                        >
                                            How it works
                                        </p>
                                    </div>
                                </div>

                                {standardLink.map(item => (
                                    <div
                                        key={item.id}
                                        className="d-flex flex-lg-column justify-content-lg-center justify-content-xl-center py-2 py-lg-0 py-xl-0 px-5 px-lg-0"
                                    >
                                        <MDBNavbarItem className="d-flex active flex-fill">
                                            <MDBNavbarLink
                                                className="d-flex justify-content-between flex-fill align-self-center"
                                                aria-current="page"
                                                onClick={() => {
                                                    router.push(item.path);
                                                }}
                                            >
                                                <div className="d-flex flex-row align-self-center">
                                                    <MDBIcon
                                                        className={`${styles.mobile} ${styles.overlay} square rounded-6 p-2 d-lg-none align-self-center me-4 text-primary`}
                                                        far
                                                        icon="star"
                                                    />
                                                    <p
                                                        className={`${styles.paragraphStyle} my-0 align-self-center`}
                                                        onMouseEnter={() =>
                                                            isSmallDropdownOpen
                                                                ? closeSmallDropDown()
                                                                : isBigDropdownOpen
                                                                ? closeBigDropdown()
                                                                : null
                                                        }
                                                    >
                                                        {item.title}
                                                    </p>
                                                </div>
                                                <MDBIcon
                                                    className={`${styles.mobile} align-self-center text-primary`}
                                                    fas
                                                    icon="chevron-circle-right"
                                                />
                                            </MDBNavbarLink>
                                        </MDBNavbarItem>
                                    </div>
                                ))}
                            </MDBCol>
                            <NavSubMenu
                                className={`${
                                    showLateralMenu ? styles.lateralMenu : ""
                                } ${styles.subMenu}`}
                                callBackTitle="Play Italy"
                                items={dropdownItems}
                                onClick={() => {
                                    setShowLateralMenu(!showLateralMenu);
                                }}
                            />
                            <MDBCol
                                className={`${styles.vertical_divider_right} d-flex justify-content-end align-items-center py-5 py-lg-0 py-xl-0`}
                                xl={3}
                                style={{
                                    paddingLeft: 0,
                                    marginLeft: "-70px",
                                    paddingRight: "25px",
                                }}
                            >
                                {showCtaButton && (
                                    <MDBBtn
                                        outline
                                        rounded
                                        color="white"
                                        className={`${styles.button_highlight} ${styles.paragraphStyleWhite} align-self-center p-2 ms-auto me-0`}
                                        size="sm"
                                        type="button"
                                        id="navigate-tours"
                                        onClick={handleCtaButtonClick}
                                    >
                                        {ctaButtonText}
                                        <contactsSvg.PencilLine
                                            className={`${styles.svgWhite} mx-2`}
                                            alt=""
                                        />
                                    </MDBBtn>
                                )}
                            </MDBCol>
                            <MDBCol
                                className="d-inline-flex py-0 align-items-center justify-content-start gap-1 ps-2"
                                xl={2}
                            >
                                {/* Currency Selector - Multi-Currency Feature */}
                                <div className="align-self-center me-2">
                                    <CurrencySelector />
                                </div>

                                {/* Search Icon */}
                                <MDBBtn
                                    tag="button"
                                    color="none"
                                    className={`align-self-center ${styles.iconButton}`}
                                    onClick={handleSearchClick}
                                    aria-label="Search"
                                >
                                    <SearchLine className={styles.svg} />
                                </MDBBtn>

                                {/* Wishlist & User Icons */}
                                {buttonIcons.map(element => (
                                    <MDBBtn
                                        key={element.id}
                                        tag="a"
                                        color="none"
                                        className={`align-self-center ${styles.iconButton}`}
                                        onClick={() => element.onClick()}
                                    >
                                        <element.icon className={styles.svg} />
                                    </MDBBtn>
                                ))}
                            </MDBCol>
                        </MDBNavbarNav>
                    </MDBCollapse>
                </MDBContainer>
            </MDBNavbar>
        );
    }
);

export default NavBar;
